import React, { useState, useEffect, useCallback } from 'react';
import { Moon, Clock, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');
const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const DAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const STATES = [
  { k: 'enuso', label: 'En uso', color: 'var(--accent)' },
  { k: 'ociosa', label: 'Ociosa', color: 'var(--green)' },
  { k: 'standby', label: 'Standby', color: 'var(--amber)' },
  { k: 'off', label: 'Apagada', color: 'var(--ink-3)' },
];
const COLOR = Object.fromEntries(STATES.map((s) => [s.k, s.color]));
const NODATA = '#eef2f6'; // gris claro = sin información

function cellColor(b, view) {
  if (!b || !b.samples) return NODATA;
  if (view === 'apt') return `rgba(13,148,136,${(0.08 + (b.suitability || 0) * 0.92).toFixed(3)})`;
  return COLOR[b.dominant] || NODATA;
}

// Ventana recomendada: corrida de horas (promedio de la semana) con mayor "% disponible".
function bestWindow(buckets) {
  const byHour = Array.from({ length: 24 }, () => ({ s: 0, n: 0 }));
  buckets.forEach((b) => { if (b.samples) { byHour[b.hour].s += b.suitability; byHour[b.hour].n++; } });
  const good = byHour.map((h) => (h.n ? h.s / h.n : 0) >= 0.6);
  let best = null, run = [];
  for (let i = 0; i < 48; i++) {
    const h = i % 24;
    if (good[h]) run.push(h); else { if (!best || run.length > best.length) best = run.slice(); run = []; }
    if (i >= 24 && (!best || run.length > best.length) && run.length) best = run.slice();
  }
  if (!best || !best.length || best.length >= 24) return null;
  const p = (n) => String(n).padStart(2, '0') + ':00';
  return `${p(best[0])}–${p((best[best.length - 1] + 1) % 24)}`;
}

// Mejor franja para APLICAR CAMBIOS/actualizaciones por sucursal. Estrategia robusta ante
// poca historia: se agrega por HORA (a través de los días), ponderando por volumen de datos,
// y se busca cuándo los equipos están online e inactivos (ociosa = alcanzables + sin molestar)
// evitando las horas de actividad de usuario (local abierto → riesgo de interrumpir). Se marca
// esa banda horaria en TODOS los días con datos (recurrente), que es lo defendible con ~1 semana.
function applyWindow(buckets) {
  const H = Array.from({ length: 24 }, () => ({ oc: 0, en: 0, w: 0 }));
  let totalW = 0;
  buckets.forEach((b) => { if (b.samples > 0) { H[b.hour].oc += b.ociosa * b.samples; H[b.hour].en += b.enuso * b.samples; H[b.hour].w += b.samples; totalW += b.samples; } });
  if (totalW <= 0) return null;
  const avgW = totalW / 24;
  const hasData = (h) => H[h].w >= avgW * 0.3;
  const enusoF = (h) => H[h].w ? H[h].en / H[h].w : 0;
  const onlineF = (h) => H[h].w ? (H[h].oc + H[h].en) / H[h].w : 0;
  // Entre las horas con equipos ONLINE (alcanzables), preferir las de MENOR actividad de usuario
  // (nadie trabajando = más seguro para reiniciar/actualizar). Score = 1 - actividad.
  const score = H.map((_, h) => (hasData(h) && onlineF(h) >= 0.4) ? (1 - enusoF(h)) : -1);
  let peak = -1, peakH = -1;
  score.forEach((s, h) => { if (s > peak) { peak = s; peakH = h; } });
  if (peak < 0) return null;                          // no hay horas con equipos online + datos
  const th = Math.max(0.6, peak - 0.15);              // banda contigua de baja actividad
  // corrida contigua (con wrap de medianoche) alrededor del pico, máx 5 horas
  let lo = peakH, hi = peakH; const win = new Set([peakH]);
  for (let i = 0; i < 4 && win.size < 5; i++) { const h = (lo - 1 + 24) % 24; if (score[h] >= th) { win.add(h); lo = h; } else break; }
  for (let i = 0; i < 4 && win.size < 5; i++) { const h = (hi + 1) % 24; if (score[h] >= th) { win.add(h); hi = h; } else break; }
  // celdas rojas = esas horas en los días con datos
  const keys = new Set(); const daysWith = new Set();
  buckets.forEach((b) => { if (win.has(b.hour) && b.samples > 0) { keys.add(b.dow * 24 + b.hour); daysWith.add(b.dow); } });
  if (!keys.size) return null;
  const p = (n) => String(n).padStart(2, '0') + ':00';
  const dset = [...daysWith].sort((a, b) => a - b);
  const dayLabel = dset.length >= 7 ? 'Todos los días'
    : dset.length >= 5 && dset.every((d) => d <= 4) ? 'Lun a Vie'
    : dset.map((d) => DAYS[d]).join(', ');
  const ociosaPct = Math.round(H[peakH].oc / H[peakH].w * 100);
  return { keys, label: `${dayLabel} · ${p(lo)}–${p((hi + 1) % 24)}`, ociosa: ociosaPct };
}

export default function Disponibilidad() {
  const [view, setView] = useState('dom');
  const [days, setDays] = useState(28);
  const [locationId, setLocationId] = useState('');
  const [assetId, setAssetId] = useState('');
  const [locations, setLocations] = useState([]);
  const [assets, setAssets] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/locations`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : [])).then((rows) => setLocations(Array.isArray(rows) ? rows : (rows.locations || []))).catch(() => {});
    fetch(`${API_URL}/assets?agent_type=windows&limit=1000`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : [])).then((rows) => {
        const list = Array.isArray(rows) ? rows : (rows.assets || rows.data || []);
        setAssets(list.filter((a) => a.hostname).map((a) => ({ id: a.id, hostname: a.hostname, location_id: a.location_id })));
      }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    const qs = new URLSearchParams({ days: String(days) });
    if (assetId) qs.set('asset_id', assetId);
    else if (locationId) qs.set('location_id', locationId);
    fetch(`${API_URL}/power/heatmap?${qs}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('No se pudo cargar'))))
      .then(setData).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [days, locationId, assetId]);

  useEffect(() => { load(); }, [load]);

  const bmap = {};
  (data?.buckets || []).forEach((b) => { bmap[b.dow * 24 + b.hour] = b; });
  const hasData = data && data.stats && (data.stats.enuso_pct + data.stats.ociosa_pct + data.stats.standby_pct + data.stats.off_pct) > 0;
  const rec = hasData ? bestWindow(data.buckets) : null;
  const apply = hasData ? applyWindow(data.buckets) : null;
  const scopeName = assetId ? (assets.find((a) => a.id === assetId)?.hostname || 'el equipo')
    : locationId ? (locations.find((l) => l.id === locationId)?.name || 'la sucursal') : 'toda la flota';
  const coverage = hasData ? Math.round(data.buckets.filter((x) => x.samples > 0).length / 168 * 100) : 0;
  const assetsForLoc = locationId ? assets.filter((a) => a.location_id === locationId) : assets;

  const S = styles;
  return (
    <div style={S.wrap}>
      <div style={S.head}>
        <div>
          <h1 style={S.title}>Disponibilidad &amp; ventanas de mantenimiento</h1>
          <div style={S.sub}>Estado por hora × día · presencia + idle del agente y eventos de energía</div>
        </div>
        <div style={S.filters}>
          <select value={locationId} onChange={(e) => { setLocationId(e.target.value); setAssetId(''); }} style={S.sel}>
            <option value="">Todas las sucursales</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          <select value={assetId} onChange={(e) => setAssetId(e.target.value)} style={S.sel}>
            <option value="">Todos los equipos</option>
            {assetsForLoc.map((a) => <option key={a.id} value={a.id}>{a.hostname}</option>)}
          </select>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={S.sel}>
            <option value={7}>Últimos 7 días</option>
            <option value={14}>Últimos 14 días</option>
            <option value={28}>Últimos 28 días</option>
          </select>
        </div>
      </div>

      <div style={S.toggle}>
        <button onClick={() => setView('dom')} style={{ ...S.tbtn, ...(view === 'dom' ? S.tbtnOn : {}) }}>Estado dominante</button>
        <button onClick={() => setView('apt')} style={{ ...S.tbtn, ...(view === 'apt' ? S.tbtnOn : {}) }}>% disponible</button>
      </div>

      {loading && <div style={S.info}>Cargando…</div>}
      {error && !loading && <div style={S.info}><AlertCircle size={15} style={{ verticalAlign: -2 }} /> {error}</div>}

      {!loading && !error && !hasData && (
        <div style={S.empty}>
          <Moon size={26} color="var(--ink-3)" />
          <div style={{ fontWeight: 600, marginTop: 8 }}>Todavía no hay datos</div>
          <div style={S.emptySub}>
            El heatmap se puebla con la presencia + idle que reportan los agentes 2.4+. Se irá
            completando a medida que los equipos reporten{data ? ` (${data.asset_count} en el alcance)` : ''}.
          </div>
        </div>
      )}

      {!loading && !error && hasData && (
        <>
          <div style={S.strip}>
            {STATES.map((s) => (
              <div key={s.k} style={S.tile}>
                <div style={S.tileV}>{data.stats[`${s.k}_pct`]}%</div>
                <div style={S.tileL}><span style={{ ...S.dot, background: s.color }} />{s.label}</div>
              </div>
            ))}
            <div style={{ ...S.tile, background: '#f0fdfa' }}>
              <div style={{ ...S.tileV, color: '#0d9488' }}>{data.stats.disponible_pct}%</div>
              <div style={S.tileL}>Disponible</div>
            </div>
          </div>

          <div style={S.gridwrap}>
            <div style={S.grid}>
              <div />
              {Array.from({ length: 24 }, (_, h) => (
                <div key={h} style={S.hhdr}>{h % 3 === 0 ? String(h).padStart(2, '0') : ''}</div>
              ))}
              {DAYS.map((dn, d) => (
                <React.Fragment key={dn}>
                  <div style={S.daylbl}>{dn}</div>
                  {Array.from({ length: 24 }, (_, h) => {
                    const b = bmap[d * 24 + h];
                    const reco = b && b.samples && (view === 'apt' ? b.suitability >= 0.6 : (b.dominant === 'ociosa' || b.dominant === 'standby'));
                    const isApply = apply && apply.keys.has(d * 24 + h);
                    return (
                      <div key={h}
                        title={b && b.samples
                          ? `${dn} ${String(h).padStart(2, '0')}:00 · En uso ${Math.round(b.enuso * 100)}% · Ociosa ${Math.round(b.ociosa * 100)}% · Standby ${Math.round(b.standby * 100)}% · Apagada ${Math.round(b.off * 100)}% · disponible ${Math.round(b.suitability * 100)}%${isApply ? ' · ★ mejor franja para aplicar cambios' : ''}`
                          : `${dn} ${String(h).padStart(2, '0')}:00 · sin datos`}
                        style={{ ...S.cell, background: cellColor(b, view), boxShadow: isApply ? 'inset 0 0 0 2.5px #dc2626' : reco ? 'inset 0 0 0 1.5px rgba(13,148,136,.9)' : 'none' }} />
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div style={S.legend}>
            {view === 'dom'
              ? <>
                  {STATES.map((s) => <span key={s.k} style={S.lg}><span style={{ ...S.dot, background: s.color }} />{s.label}</span>)}
                  <span style={S.lg}><span style={{ ...S.dot, background: NODATA, border: '1px solid var(--border)' }} />Sin información</span>
                </>
              : <span style={S.lg}>Menos <span style={S.ramp} /> Más disponible · <span style={{ color: 'var(--ink-2)' }}>borde = franja recomendada</span></span>}
            {apply && <span style={S.lg}><span style={{ ...S.dot, background: 'transparent', boxShadow: 'inset 0 0 0 2px #dc2626', borderRadius: 2 }} />Mejor franja para aplicar cambios</span>}
            <span style={{ color: 'var(--ink-2)', marginLeft: 'auto' }}>Cobertura: <b>{coverage}%</b> de las franjas con datos</span>
          </div>

          {apply && (
            <div style={S.recoRed}>
              <Clock size={15} style={{ verticalAlign: -2, color: '#dc2626' }} /> <b>Mejor día y horario para aplicar cambios en {scopeName}:</b>{' '}
              <b style={{ color: '#dc2626' }}>{apply.label}</b> — es cuando los equipos están <b>online e inactivos</b> ({apply.ociosa}% ociosa): el agente los alcanza y no interrumpe al usuario. Marcado en <b style={{ color: '#dc2626' }}>rojo</b> en el diagrama.
            </div>
          )}
          {rec && (
            <div style={S.reco}>
              <Clock size={15} style={{ verticalAlign: -2 }} /> <b>Franja de mayor disponibilidad:</b> {rec}.
              <span style={{ color: 'var(--ink-2)' }}> Ociosa = mantenimiento ya (sin despertar); standby/off = requiere wake (WoL) — próximamente.</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const styles = {
  wrap: { padding: '22px 24px', maxWidth: 980 },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 14 },
  title: { fontSize: 20, fontWeight: 700, margin: 0, letterSpacing: '-.01em' },
  sub: { fontSize: 12.5, color: 'var(--ink-2)', marginTop: 3 },
  filters: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  sel: { fontSize: 13, padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)', color: 'var(--ink)' },
  toggle: { display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden', marginBottom: 16 },
  tbtn: { font: 'inherit', fontSize: 13, fontWeight: 600, color: 'var(--ink-2)', background: 'var(--bg-subtle)', border: 0, padding: '8px 15px', cursor: 'pointer' },
  tbtnOn: { background: 'var(--ink)', color: 'var(--surface)' },
  strip: { display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 1, background: 'var(--border)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', marginBottom: 16 },
  tile: { background: 'var(--surface)', padding: '12px 14px' },
  tileV: { fontSize: 20, fontWeight: 700, lineHeight: 1 },
  tileL: { fontSize: 11.5, color: 'var(--ink-2)', marginTop: 5, display: 'flex', alignItems: 'center', gap: 6 },
  dot: { width: 9, height: 9, borderRadius: 2, display: 'inline-block' },
  gridwrap: { overflowX: 'auto' },
  grid: { display: 'grid', gridTemplateColumns: '34px repeat(24,1fr)', gap: 3, minWidth: 640 },
  hhdr: { fontSize: 9.5, color: 'var(--ink-3)', textAlign: 'center' },
  daylbl: { fontSize: 11.5, color: 'var(--ink-2)', fontWeight: 600, display: 'flex', alignItems: 'center', height: 22 },
  cell: { height: 22, borderRadius: 3, cursor: 'default' },
  legend: { display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12.5, marginTop: 12, alignItems: 'center' },
  lg: { display: 'flex', alignItems: 'center', gap: 6 },
  ramp: { display: 'inline-block', width: 120, height: 10, borderRadius: 3, background: 'linear-gradient(90deg,rgba(13,148,136,.10),rgba(13,148,136,1))', margin: '0 4px' },
  recoRed: { fontSize: 12.5, background: 'rgba(220,38,38,.06)', border: '1px solid rgba(220,38,38,.28)', borderLeft: '3px solid #dc2626', borderRadius: 8, padding: '9px 12px', marginTop: 14, lineHeight: 1.55 },
  reco: { fontSize: 12.5, background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderLeft: '3px solid #0d9488', borderRadius: 8, padding: '9px 12px', marginTop: 10 },
  info: { fontSize: 13, color: 'var(--ink-2)', padding: '20px 0' },
  empty: { textAlign: 'center', padding: '46px 20px', border: '1px dashed var(--border)', borderRadius: 12, color: 'var(--ink)' },
  emptySub: { fontSize: 12.5, color: 'var(--ink-2)', marginTop: 6, maxWidth: 460, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5 },
};
